import type { StaffMemoryFact } from "@lafayette/schemas";
import { useId } from "react";

import { cn } from "../../ui/cn";
import { confirmedPhrase } from "../format";
import { kindLabel, provenanceLabels } from "../labels";
import { FactBadges, FactValue } from "./parts";

// The current memory next to the candidate that would change or contradict it. Both sides say where
// they came from. The candidate side says in its own words when the assistant only inferred it.

function Side({
  heading,
  fact,
  now,
  valueId,
}: Readonly<{ heading: string; fact: StaffMemoryFact; now: Date; valueId?: string | undefined }>) {
  const label = provenanceLabels[fact.provenance];
  const headingId = useId();
  // A named group, not a landmark and not a heading: a review list holds many of these, and a landmark name must be unique on a page
  // (and a heading here would skip a level under the section title or the dialog title).
  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset draws its legend on the border; this is a plain card with a named group
    <div
      role="group"
      aria-labelledby={headingId}
      className="grid min-w-0 content-start gap-1.5 rounded-sm border border-border bg-muted p-4"
    >
      <p
        id={headingId}
        className="m-0 text-xs font-bold tracking-[0.06em] text-muted-foreground uppercase"
      >
        {heading}
      </p>
      <FactValue fact={fact} id={valueId} />
      <p className="m-0 text-sm text-muted-foreground">{kindLabel(fact.kind)}</p>
      <FactBadges fact={fact} />
      <p className="m-0 text-sm text-muted-foreground">{label.detail}</p>
      <p className="m-0 text-sm text-muted-foreground">
        {confirmedPhrase(fact.lastConfirmedAt, now, fact)}
      </p>
    </div>
  );
}

export function Compare({
  current,
  candidate,
  now,
  candidateValueId,
}: Readonly<{
  current: StaffMemoryFact | null;
  candidate: StaffMemoryFact;
  now: Date;
  /** The id of the element that holds the candidate's value, for `aria-describedby` of the buttons. */
  candidateValueId?: string | undefined;
}>) {
  return (
    <div
      className={cn("grid gap-4", current === null ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2")}
    >
      {current === null ? null : <Side heading="Current memory" fact={current} now={now} />}
      <Side heading="Candidate" fact={candidate} now={now} valueId={candidateValueId} />
    </div>
  );
}
